import { useEffect, useRef } from "react";
import {
  getVideoTelemetryUser,
  isVideoTelemetryActive,
  newTelemetryId,
  persistOpenSession,
  recordIssue,
  recordSessionSnapshot,
  registerPillTracker,
} from "../../services/videoTelemetry";

/**
 * What happened to one playback, recorded as it happens.
 *
 * Opt-in: a player only records when it is given a `telemetry` context (the
 * course pages do). A session is one attempt to watch one video on one screen;
 * see the backend's VideoPlaybackOutcomeClassifier for how it is judged. This
 * hook only observes — it never plays, pauses or seeks — and everything lives in
 * refs, so recording adds no renders to a player that re-renders on progress.
 */

// Buffering shorter than this is ordinary and not worth a record.
const STALL_RECORD_AFTER_MS = 1000;
// Should be playing, no buffering event, and time has not moved for this long.
const STUCK_AFTER_MS = 3000;
const WATCHDOG_MS = 1000;
const HEARTBEAT_MS = 60 * 1000;
// Snapshot a start that has produced no frame yet, so an app killed while it
// spins is still counted.
const START_CHECKPOINT_MS = 15 * 1000;
const BACKGROUND_ENDS_SESSION_MS = 10 * 60 * 1000;
// A pause within this long of the player saying why is the player's own.
const PAUSE_REASON_TTL_MS = 1000;
const PERSIST_EVERY_MS = 10 * 1000;
// A completion result can arrive after the session ended (retries).
const COMPLETION_LOOKBACK_MS = 10 * 60 * 1000;

const MEDIA_ERROR_CODES = {
  1: "MEDIA_ERR_ABORTED",
  2: "MEDIA_ERR_NETWORK",
  3: "MEDIA_ERR_DECODE",
  4: "MEDIA_ERR_SRC_NOT_SUPPORTED",
};

const round = (value, digits = 2) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const factor = 10 ** digits;
  return Math.round(n * factor) / factor;
};

const numberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

function parseExpiry(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return value > 1e12 ? value : value * 1000;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function bufferedAhead(el) {
  try {
    const t = el.currentTime;
    for (let i = 0; i < el.buffered.length; i += 1) {
      if (el.buffered.start(i) <= t + 0.1 && el.buffered.end(i) >= t) {
        return round(el.buffered.end(i) - t);
      }
    }
  } catch (_) {
    /* ignore */
  }
  return 0;
}

function mediaState(el) {
  if (!el) return {};
  return {
    ready_state: el.readyState,
    network_state: el.networkState,
    buffer_ahead_s: bufferedAhead(el),
  };
}

function environment() {
  try {
    return {
      online: typeof navigator !== "undefined" ? navigator.onLine : null,
      network_type: navigator?.connection?.effectiveType ?? null,
      visible: document.visibilityState !== "hidden",
    };
  } catch (_) {
    return {};
  }
}

function frameCounts(el) {
  try {
    const quality = el?.getVideoPlaybackQuality?.();
    if (quality) {
      return { dropped: quality.droppedVideoFrames, total: quality.totalVideoFrames };
    }
    if (el && typeof el.webkitDroppedFrameCount === "number") {
      return { dropped: el.webkitDroppedFrameCount, total: el.webkitDecodedFrameCount };
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

/** The session as sent: internal `_` fields dropped, details copied. */
function publicFields(session) {
  const out = {};
  Object.keys(session).forEach((key) => {
    if (key[0] !== "_") out[key] = session[key];
  });
  return JSON.parse(JSON.stringify(out));
}

function createApi(propsRef, videoRef) {
  const st = {
    session: null,
    lastEnded: null,
    pauseReason: null,
    path: null,
    hiddenAt: null,
    watchdog: null,
    checkpoint: null,
  };

  const video = () => videoRef.current;

  const guard =
    (fn) =>
    (...args) => {
      try {
        return fn(...args);
      } catch (_) {
        return undefined;
      }
    };

  function emit(flush = false) {
    const s = st.session;
    if (!s) return;
    s.seq += 1;
    s.last_activity_at = Date.now();
    s._persistedAt = s.last_activity_at;
    syncStartWait(s.last_activity_at);
    recordSessionSnapshot(publicFields(s), { flush });
  }

  function stopTimers() {
    clearInterval(st.watchdog);
    clearTimeout(st.checkpoint);
    st.watchdog = null;
    st.checkpoint = null;
  }

  function issueFor(type, fields = {}) {
    const s = st.session;
    const el = video();
    const now = Date.now();

    const issue = {
      id: newTelemetryId(),
      session_id: s.id,
      uid: s.uid,
      seq: 1,
      type,
      started_at: fields.startedAt ?? now,
      duration_ms: fields.durationMs ?? null,
      position_s: el ? round(el.currentTime) : null,
      code: fields.code ?? null,
      http_status: fields.httpStatus ?? null,
      fatal: !!fields.fatal,
      recovered: fields.recovered ?? null,
      details: {
        ...mediaState(el),
        ...environment(),
        level_height: s.details.levels?.current_height ?? null,
        ...(fields.details || {}),
      },
    };

    if (fields.countsAsProblem !== false) {
      s.last_issue_at = Math.max(s.last_issue_at || 0, issue.started_at);
    }

    return issue;
  }

  function finishIssue(issue, recovered, at = Date.now()) {
    issue.seq += 1;
    issue.duration_ms = Math.max(0, at - issue.started_at);
    issue.recovered = recovered;
    recordIssue({ ...issue });

    const s = st.session;
    if (s) s.last_issue_at = Math.max(s.last_issue_at || 0, at);
  }

  function refreshOpenFlag() {
    const s = st.session;
    if (s) s.open_issue = !!((s._stall && s._stall.issue) || s._stuck || s._pause);
  }

  // The wait to start only runs while the element is asked to play and has no
  // frame yet. An autoplay the player paused itself, or a video left waiting for
  // a tap, is neither a slow start nor a failed one.
  function openAttempt(now = Date.now()) {
    const s = st.session;
    if (!s || s.first_frame_at || s._attemptAt !== null) return;
    if (document.visibilityState === "hidden") return;
    s._attemptAt = now;
    s._attempted = true;
    s.details.start_attempts = (s.details.start_attempts || 0) + 1;
  }

  function closeAttempt(now = Date.now()) {
    const s = st.session;
    if (!s || s._attemptAt === null) return;
    s._startWaitClosed += Math.max(0, now - s._attemptAt);
    s._attemptAt = null;
  }

  function syncStartWait(now = Date.now()) {
    const s = st.session;
    if (!s || !s._attempted) return;
    const running = s._attemptAt !== null ? Math.max(0, now - s._attemptAt) : 0;
    s.start_wait_ms = Math.round(s._startWaitClosed + running);
  }

  function tick() {
    const s = st.session;
    if (!s) return;

    const el = video();
    const now = Date.now();

    if (el && s.first_frame_at) {
      const t = el.currentTime || 0;
      const moved = t - (s._lastTime ?? t);
      const shouldBePlaying =
        !el.paused && !el.ended && !el.seeking && document.visibilityState !== "hidden";

      if (shouldBePlaying) {
        if (moved > 0.2) {
          // Only small forward steps count as watching; a jump is a seek.
          if (moved < 5) s.watched_s = round(s.watched_s + moved);
          s._lastTime = t;
          s._lastProgressAt = now;
          if (s._stuck) closeStuck(true, now);
        } else if (moved < -0.5) {
          s._lastTime = t;
          s._lastProgressAt = now;
        } else if (!s._stall && !s._stuck && now - s._lastProgressAt >= STUCK_AFTER_MS) {
          const issue = issueFor("stuck", {
            startedAt: s._lastProgressAt,
            details: { frames: frameCounts(el) },
          });
          s._stuck = { issue };
          s.stuck_count += 1;
          recordIssue({ ...issue });
          refreshOpenFlag();
          emit();
        }
      } else {
        // Paused, hidden or seeking: nothing to judge, restart the clock.
        s._lastTime = t;
        s._lastProgressAt = now;
      }

      // Keep an ongoing freeze's length current in case the app is killed.
      if (s._stall?.issue) {
        s.longest_stall_ms = Math.max(s.longest_stall_ms, now - s._stall.startedAt);
      }
    }

    if (el && !el.paused && now - s._lastHeartbeatAt >= HEARTBEAT_MS) {
      s._lastHeartbeatAt = now;
      emit();
    } else if (now - (s._persistedAt || 0) >= PERSIST_EVERY_MS) {
      s._persistedAt = now;
      s.last_activity_at = now;
      syncStartWait(now);
      persistOpenSession(publicFields(s));
    }
  }

  function start(intent) {
    const props = propsRef.current;
    if (!props.telemetry || !props.active || st.session || !isVideoTelemetryActive()) {
      return st.session;
    }

    const ctx = props.telemetry;
    const el = video();
    const now = Date.now();

    st.session = {
      id: newTelemetryId(),
      uid: getVideoTelemetryUser(),
      seq: 0,
      surface: ctx.surface || "other",
      course_id: numberOrNull(ctx.courseId),
      lesson_id: numberOrNull(ctx.lessonId),
      post_id: numberOrNull(ctx.postId),
      vimeo_id: ctx.vimeoId ? String(ctx.vimeoId) : null,
      path_initial: st.path,
      path_final: st.path,
      intent_at: now,
      first_frame_at: null,
      start_wait_ms: 0,
      last_activity_at: now,
      ended_at: null,
      end_reason: null,
      duration_s: null,
      watched_s: 0,
      max_position_pct: 0,
      gate_reached_at: null,
      completion_result: null,
      completion_http_status: null,
      stall_count: 0,
      stall_ms: 0,
      longest_stall_ms: 0,
      stuck_count: 0,
      stuck_ms: 0,
      unexpected_pause_count: 0,
      error_count: 0,
      fatal_error: false,
      last_error_code: null,
      link_expires_at: parseExpiry(ctx.linkExpiresAt),
      link_expired: false,
      open_issue: false,
      last_issue_at: null,
      details: { intent, slide_index: numberOrNull(ctx.slideIndex), levels: {}, seeks: 0 },
      _stall: null,
      _stuck: null,
      _pause: null,
      _attemptAt: null,
      _startWaitClosed: 0,
      _attempted: false,
      _lastTime: el ? el.currentTime || 0 : 0,
      _lastProgressAt: now,
      _lastHeartbeatAt: now,
      _persistedAt: now,
      _frames: frameCounts(el),
    };

    // Created while the element is already asked to play (autoplay underway).
    if (el && !el.paused) openAttempt(now);

    persistOpenSession(publicFields(st.session));
    st.watchdog = setInterval(guard(tick), WATCHDOG_MS);
    st.checkpoint = setTimeout(
      guard(() => {
        if (st.session && !st.session.first_frame_at) emit();
      }),
      START_CHECKPOINT_MS,
    );

    return st.session;
  }

  function end(reason, endedAt = Date.now()) {
    const s = st.session;
    if (!s) return;

    stopTimers();
    closeAttempt(endedAt);
    syncStartWait(endedAt);

    // Whatever was still going on did not recover inside this session.
    const hadOpenProblem = !!((s._stall && s._stall.issue) || s._stuck || s._pause);
    if (s._stall) closeStall(false, endedAt);
    if (s._stuck) closeStuck(false, endedAt);
    if (s._pause) closePause(false, endedAt);
    s.open_issue = hadOpenProblem;

    const frames = frameCounts(video());
    if (frames && s._frames) {
      s.details.frames = {
        dropped: Math.max(0, frames.dropped - s._frames.dropped),
        total: Math.max(0, frames.total - s._frames.total),
      };
    }

    s.ended_at = endedAt;
    s.end_reason = reason;
    s.seq += 1;
    s.last_activity_at = Math.max(s.last_activity_at, endedAt);

    const final = publicFields(s);
    recordSessionSnapshot(final, { flush: true });

    st.lastEnded = { snapshot: final, at: Date.now() };
    st.session = null;
  }

  function markFirstFrame() {
    const s = st.session;
    if (!s || s.first_frame_at) return;
    s.first_frame_at = Date.now();
    if (!s._attempted) {
      // The request to play went unseen (no play event): count from the intent.
      s._startWaitClosed = Math.max(0, s.first_frame_at - s.intent_at);
      s._attempted = true;
    }
    closeAttempt(s.first_frame_at);
    syncStartWait(s.first_frame_at);
    s._lastProgressAt = s.first_frame_at;
    s._lastTime = video()?.currentTime ?? 0;
    clearTimeout(st.checkpoint);
    emit();
  }

  function checkGate(pct) {
    const s = st.session;
    const gate = propsRef.current.gateAt;
    if (!s || gate === null || gate === undefined || s.gate_reached_at) return;
    if (Number(pct) >= gate) {
      s.gate_reached_at = Date.now();
      emit();
    }
  }

  function openStall() {
    const s = st.session;
    if (!s || !s.first_frame_at || s._stall || s._stuck) return;

    const stall = { startedAt: Date.now(), issue: null, timer: null };
    stall.timer = setTimeout(
      guard(() => {
        const current = st.session;
        if (!current || current._stall !== stall) return;
        stall.issue = issueFor("stall", { startedAt: stall.startedAt });
        current.stall_count += 1;
        recordIssue({ ...stall.issue });
        refreshOpenFlag();
        emit();
      }),
      STALL_RECORD_AFTER_MS,
    );
    s._stall = stall;
  }

  function closeStall(recovered, at = Date.now()) {
    const s = st.session;
    const stall = s?._stall;
    if (!stall) return;

    clearTimeout(stall.timer);
    s._stall = null;
    if (!stall.issue) return; // ordinary buffering, never recorded

    const duration = Math.max(0, at - stall.startedAt);
    s.stall_ms += duration;
    s.longest_stall_ms = Math.max(s.longest_stall_ms, duration);
    finishIssue(stall.issue, recovered, at);
    refreshOpenFlag();
    if (recovered) emit();
  }

  function closeStuck(recovered, at = Date.now()) {
    const s = st.session;
    const stuck = s?._stuck;
    if (!stuck) return;

    s._stuck = null;
    s.stuck_ms += Math.max(0, at - stuck.issue.started_at);
    finishIssue(stuck.issue, recovered, at);
    refreshOpenFlag();
    if (recovered) emit();
  }

  function closePause(recovered, at = Date.now()) {
    const s = st.session;
    const pause = s?._pause;
    if (!pause) return;

    s._pause = null;
    finishIssue(pause.issue, recovered, at);
    refreshOpenFlag();
  }

  function recordError({ code, httpStatus = null, fatal, details = {} }) {
    const s = st.session;
    if (!s) return;

    const now = Date.now();
    const expired = !!(s.link_expires_at && now >= s.link_expires_at);

    s.error_count += 1;
    s.last_error_code = code;
    if (expired) s.link_expired = true;

    const issue = issueFor("error", {
      code,
      httpStatus,
      fatal,
      durationMs: 0,
      recovered: fatal ? false : null,
      details: { ...details, link_expired: expired },
    });
    recordIssue({ ...issue });

    if (fatal) {
      s.fatal_error = true;
      end("error");
    } else {
      emit();
    }
  }

  // ── <video> element events ────────────────────────────────────────────────

  function onPlay() {
    const s = st.session || start("play");
    if (!s) return;
    openAttempt();
    if (s._pause) closePause(true);
  }

  function onPlaying() {
    const s = st.session || start("play");
    if (!s) return;
    closeStall(true);
    if (!s.first_frame_at) markFirstFrame();
    if (s._pause) closePause(true);
  }

  function onWaiting() {
    const el = video();
    if (el?.seeking) return;
    if (el && !el.paused) openAttempt();
    openStall();
  }

  function onSeeking() {
    const s = st.session;
    if (!s) return;
    s.details.seeks = (s.details.seeks || 0) + 1;
    // A seek the member asked for is not a freeze.
    if (s._stall && !s._stall.issue) closeStall(true);
    if (s._stuck) closeStuck(true);
  }

  function onPause() {
    const s = st.session;
    const el = video();
    // Paused before the first frame: the wait to start stops here.
    closeAttempt();
    if (!s || !el || el.ended || el.seeking) return;

    const marked = st.pauseReason;
    st.pauseReason = null;
    const recent = marked && Date.now() - marked.at <= PAUSE_REASON_TTL_MS;
    const props = propsRef.current;
    const visible = document.visibilityState !== "hidden";

    // Another player took over while this one was the one on screen: the
    // member saw this video stop without doing anything.
    const takenOver = recent && marked.reason === "exclusive_takeover" && props.active && visible;

    if (recent && !takenOver) return;
    if (!visible || !props.attributablePauses || !props.active) return;
    if (el.webkitDisplayingFullscreen) return; // iOS's own fullscreen controls
    if (!s.first_frame_at || s._pause) return;

    const issue = issueFor("unexpected_pause", { code: takenOver ? "exclusive_takeover" : null });
    s._pause = { issue };
    s.unexpected_pause_count += 1;
    recordIssue({ ...issue });
    refreshOpenFlag();
    emit();
  }

  function onEnded() {
    const s = st.session;
    if (!s) return;
    s.max_position_pct = 1;
    end("ended");
  }

  function onMediaError() {
    const el = video();
    if (!el?.error) return;
    // hls.js reports its own failures, with more detail, through attachHls().
    if (st.path === "hls_js_mse" || st.path === "hls_js_mms") return;

    const s = st.session || start("error");
    if (!s) return;

    recordError({
      code: MEDIA_ERROR_CODES[el.error.code] || `MEDIA_ERR_${el.error.code}`,
      // Progressive and Apple native HLS have nothing left to fall back to.
      fatal: true,
      details: { message: String(el.error.message || "").slice(0, 160) },
    });
  }

  function onTimeUpdate() {
    const s = st.session;
    const el = video();
    if (!s || !el) return;

    if (!s.first_frame_at && el.currentTime > 0) markFirstFrame();

    const duration = el.duration;
    if (Number.isFinite(duration) && duration > 0) {
      s.duration_s = round(duration);
      const pct = Math.min(1, el.currentTime / duration);
      if (pct > s.max_position_pct) s.max_position_pct = round(pct, 4);
      checkGate(pct);
    }
  }

  // ── Player hooks ──────────────────────────────────────────────────────────

  function setPath(path) {
    st.path = path;
    const s = st.session;
    if (s) {
      if (!s.path_initial) s.path_initial = path;
      s.path_final = path;
    }
  }

  function markPauseReason(reason) {
    st.pauseReason = { reason, at: Date.now() };
  }

  function attachHls(hls, Hls) {
    if (!propsRef.current.telemetry || !hls || !Hls?.Events) return;

    hls.on(
      Hls.Events.MANIFEST_PARSED,
      guard((_event, data) => {
        const levels = data?.levels || hls.levels || [];
        const top = levels.reduce((max, level) => Math.max(max, level?.height || 0), 0);
        st.ladderMaxHeight = top || null;
        if (st.session) st.session.details.levels.ladder_max_height = st.ladderMaxHeight;
      }),
    );

    hls.on(
      Hls.Events.LEVEL_SWITCHED,
      guard((_event, data) => {
        const s = st.session;
        if (!s) return;
        const height = hls.levels?.[data?.level]?.height || null;
        const levels = s.details.levels;
        if (levels.start_height === undefined) levels.start_height = height;
        else levels.switches = (levels.switches || 0) + 1;
        levels.current_height = height;
        levels.max_height = Math.max(levels.max_height || 0, height || 0);
        levels.ladder_max_height = levels.ladder_max_height ?? st.ladderMaxHeight ?? null;
        const cap = hls.autoLevelCapping;
        levels.cap_height = cap >= 0 ? hls.levels?.[cap]?.height ?? null : null;
      }),
    );

    hls.on(
      Hls.Events.FRAG_LOADED,
      guard(() => {
        const s = st.session;
        if (!s) return;
        const kbps = Math.round((hls.bandwidthEstimate || 0) / 1000);
        if (!kbps) return;
        const band = s.details.bandwidth || (s.details.bandwidth = {});
        band.min_kbps = band.min_kbps ? Math.min(band.min_kbps, kbps) : kbps;
        band.max_kbps = Math.max(band.max_kbps || 0, kbps);
      }),
    );

    hls.on(
      Hls.Events.ERROR,
      guard((_event, data) => {
        if (!data?.fatal) {
          const s = st.session;
          if (!s) return;
          const key = String(data?.details || "unknown").slice(0, 40);
          const bag = s.details.hls_nonfatal || (s.details.hls_nonfatal = {});
          if (bag[key] || Object.keys(bag).length < 10) bag[key] = (bag[key] || 0) + 1;
          return;
        }

        const s = st.session || start("error");
        if (!s) return;

        recordError({
          code: `hls:${data?.type || "unknown"}:${data?.details || "unknown"}`,
          httpStatus: data?.response?.code ?? null,
          fatal: !propsRef.current.canFallback,
          details: { source: "hls.js" },
        });
      }),
    );
  }

  function attachSdk(player) {
    if (!propsRef.current.telemetry || !player) return;
    setPath("vimeo_embed");

    let lastSeconds = null;
    const on = (event, fn) => {
      try {
        player.on(event, guard(fn));
      } catch (_) {
        /* ignore */
      }
    };

    on("play", () => {
      const s = st.session || start("play");
      if (!s) return;
      openAttempt();
      if (s._pause) closePause(true);
    });
    on("pause", () => closeAttempt());
    on("bufferstart", openStall);
    on("bufferend", () => closeStall(true));
    on("timeupdate", ({ seconds, duration, percent } = {}) => {
      const s = st.session;
      if (!s) return;
      if (!s.first_frame_at && seconds > 0) markFirstFrame();
      if (Number.isFinite(duration) && duration > 0) s.duration_s = round(duration);
      const pct = Number(percent);
      if (Number.isFinite(pct)) {
        if (pct > s.max_position_pct) s.max_position_pct = round(Math.min(1, pct), 4);
        checkGate(pct);
      }
      if (lastSeconds !== null) {
        const moved = seconds - lastSeconds;
        if (moved > 0 && moved < 5) s.watched_s = round(s.watched_s + moved);
      }
      lastSeconds = seconds;
    });
    on("ended", () => {
      const s = st.session;
      if (s) s.max_position_pct = 1;
      end("ended");
    });
    on("error", (error) => {
      const s = st.session || start("error");
      if (!s) return;
      recordError({
        code: `vimeo:${error?.name || "error"}`,
        fatal: true,
        details: { message: String(error?.message || "").slice(0, 120) },
      });
    });
  }

  function recordFallback({ reason, httpStatus = null, to = "progressive" }) {
    markPauseReason("fallback");
    const from = st.path;
    setPath(to);

    const s = st.session;
    if (!s) return;

    s.details.fallbacks = [...(s.details.fallbacks || []), { from, to, reason }].slice(-5);
    const issue = issueFor("fallback", {
      code: reason,
      httpStatus,
      durationMs: 0,
      recovered: true,
      countsAsProblem: false,
    });
    recordIssue({ ...issue });
    emit();
  }

  function recordPlayRejected(error) {
    const name = error?.name || "PlayError";
    // Interrupted by our own pause (the visibility gate, a slide change): not a
    // refusal, but counted — an autoplay that keeps being interrupted is a video
    // the member finds sitting at 0:00.
    if (name === "AbortError") {
      if (st.session) st.session.details.play_aborted = (st.session.details.play_aborted || 0) + 1;
      return;
    }

    const s = st.session || start("autoplay");
    if (!s) return;

    const issue = issueFor("play_rejected", {
      code: name,
      durationMs: 0,
      recovered: false,
      countsAsProblem: false,
      details: { message: String(error?.message || "").slice(0, 120) },
    });
    recordIssue({ ...issue });
    emit();
  }

  function onUserAction(action) {
    if (action === "pause") markPauseReason("user");
    else if (action === "play") start("tap");
  }

  function onPillCompletion({ result, httpStatus = null } = {}) {
    const outcome = result === "failed" ? "failed" : "ok";
    const code = outcome === "failed" ? (httpStatus ? `HTTP_${httpStatus}` : "network") : null;
    const s = st.session;

    if (s) {
      s.completion_result = outcome;
      s.completion_http_status = numberOrNull(httpStatus);
      if (outcome === "failed") {
        const issue = issueFor("completion_failed", {
          code,
          httpStatus: numberOrNull(httpStatus),
          durationMs: 0,
          recovered: false,
          countsAsProblem: false,
        });
        recordIssue({ ...issue });
      }
      emit();
      return;
    }

    const last = st.lastEnded;
    if (!last || Date.now() - last.at > COMPLETION_LOOKBACK_MS) return;

    const snapshot = {
      ...last.snapshot,
      seq: last.snapshot.seq + 1,
      completion_result: outcome,
      completion_http_status: numberOrNull(httpStatus),
    };
    st.lastEnded = { snapshot, at: last.at };
    recordSessionSnapshot(snapshot);

    if (outcome === "failed") {
      recordIssue({
        id: newTelemetryId(),
        session_id: snapshot.id,
        uid: snapshot.uid,
        seq: 1,
        type: "completion_failed",
        started_at: Date.now(),
        duration_ms: 0,
        position_s: null,
        code,
        http_status: numberOrNull(httpStatus),
        fatal: false,
        recovered: false,
        details: environment(),
      });
    }
  }

  function onHidden() {
    st.hiddenAt = Date.now();
    closeAttempt(st.hiddenAt);
    if (st.session) emit(true);
  }

  function onVisible() {
    const hiddenAt = st.hiddenAt;
    st.hiddenAt = null;
    if (st.session && hiddenAt && Date.now() - hiddenAt >= BACKGROUND_ENDS_SESSION_MS) {
      end("app_background", hiddenAt);
    }
    const el = video();
    if (el && !el.paused) openAttempt();
  }

  return {
    // for CustomVimeoPlayer
    markIntent: guard(start),
    markPauseReason: guard(markPauseReason),
    setPath: guard(setPath),
    attachHls: guard(attachHls),
    attachSdk: guard(attachSdk),
    recordFallback: guard(recordFallback),
    recordPlayRejected: guard(recordPlayRejected),
    onUserAction: guard(onUserAction),
    end: guard(end),
    // for the hook's own effects
    _events: {
      play: guard(onPlay),
      playing: guard(onPlaying),
      waiting: guard(onWaiting),
      seeking: guard(onSeeking),
      pause: guard(onPause),
      ended: guard(onEnded),
      error: guard(onMediaError),
      timeupdate: guard(onTimeUpdate),
    },
    _onPillCompletion: guard(onPillCompletion),
    _onHidden: guard(onHidden),
    _onVisible: guard(onVisible),
  };
}

/**
 * @param {object}  options
 * @param {object|null} options.telemetry  course context: { surface, courseId, lessonId, postId, vimeoId, slideIndex, linkExpiresAt }
 * @param {object}  options.htmlVideoRef   the player's <video> ref
 * @param {boolean} options.active         this player is the one the member is on
 * @param {boolean|null} options.isWatching
 * @param {boolean} options.isLeavingView
 * @param {boolean} options.attributablePauses  every member pause goes through our own controls
 * @param {number|null} options.gateAt     completion threshold (0..1), when the video has one
 * @param {string}  options.sourceKey      changes when the player is given another video
 * @param {boolean} options.canFallback    an adaptive-stream failure can still drop to the MP4
 * @param {boolean} options.hasNativeElement the player renders a <video> element
 */
export default function useVideoTelemetry({
  telemetry,
  htmlVideoRef,
  active,
  isWatching,
  isLeavingView,
  attributablePauses,
  gateAt,
  sourceKey,
  canFallback,
  hasNativeElement,
}) {
  const enabled = !!telemetry;

  const propsRef = useRef({});
  propsRef.current = {
    telemetry,
    active,
    isWatching,
    isLeavingView,
    attributablePauses,
    gateAt,
    canFallback,
  };

  const apiRef = useRef(null);
  if (apiRef.current === null) apiRef.current = createApi(propsRef, htmlVideoRef);
  const api = apiRef.current;

  // Media element events.
  useEffect(() => {
    const el = htmlVideoRef.current;
    if (!enabled || !hasNativeElement || !el) return undefined;

    const handlers = api._events;
    Object.entries(handlers).forEach(([event, handler]) => el.addEventListener(event, handler));

    return () => {
      Object.entries(handlers).forEach(([event, handler]) =>
        el.removeEventListener(event, handler),
      );
    };
  }, [enabled, hasNativeElement, api, htmlVideoRef]);

  // Leaving the video — another slide, another page — ends the session.
  useEffect(() => {
    if (!enabled || active) return;
    const props = propsRef.current;
    let reason = "other";
    if (props.isLeavingView) reason = "navigation";
    else if (props.isWatching === false) reason = "slide_change";
    api.end(reason);
  }, [enabled, active, api]);

  // A different video in the same player is a different session.
  const sourceRef = useRef(sourceKey);
  useEffect(() => {
    if (sourceRef.current === sourceKey) return;
    sourceRef.current = sourceKey;
    api.end("source_change");
  }, [sourceKey, api]);

  // Background: snapshot on the way out; a long absence ends the session.
  useEffect(() => {
    if (!enabled) return undefined;

    const onVisibility = () =>
      document.visibilityState === "hidden" ? api._onHidden() : api._onVisible();

    document.addEventListener("visibilitychange", onVisibility);
    // Capacitor dispatches these on document for the native app lifecycle.
    document.addEventListener("pause", api._onHidden);
    document.addEventListener("resume", api._onVisible);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("pause", api._onHidden);
      document.removeEventListener("resume", api._onVisible);
    };
  }, [enabled, api]);

  // The pill page reports whether saving this pill's completion worked.
  const postId = telemetry?.postId ?? null;
  useEffect(() => {
    if (!enabled || postId === null || postId === undefined) return undefined;
    return registerPillTracker(postId, api._onPillCompletion);
  }, [enabled, postId, api]);

  useEffect(() => () => api.end("unmount"), [api]);

  return api;
}
